import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Flex, Select, Text, TextInput } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import COLORS from '../../constants/colors';
import {
  createTerritory,
  toggleTerritory,
  updateTerritory,
} from '../../services/territories';
import { NotificationUtil } from '../../utils/notifications';
import {
  AddTerritorySchema,
  UpdateTerritorySchema,
} from '../../validators/territory';

const TerritoriesForm = ({
  defaultValues,
  update,
  onUpdate,
  regionPagelessdata,
  areaPagelessData,
  distributorPagelessdata,
}) => {
  const queryClient = useQueryClient();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    defaultValues: defaultValues ? defaultValues : {},
    resolver: yupResolver(update ? UpdateTerritorySchema : AddTerritorySchema),
    mode: 'onChange',
  });

  const regionId = useWatch({
    control,
    name: 'regionId',
  });
  const areaId = useWatch({
    control,
    name: 'areaId',
  });

  useEffect(() => {
    reset();
  }, []);

  const onSubmit = (data) => {
    ConfirmModal(data);
  };

  const ConfirmModal = (values) => {
    openConfirmModal({
      title: 'Confirm',
      styles: () => ({
        title: {
          fontSize: '22px',
          fontWeight: 'bold',
        },
      }),
      children: (
        <Text size="sm">
          Are you sure you {update ? 'update' : 'create'} this territory?
        </Text>
      ),
      confirmProps: { color: 'red' },
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onConfirm: () => {
        if (update) {
          updateMutate({
            id: defaultValues.id,
            ...values,
          });
        } else {
          createMutate(values);
        }
      },
    });
  };

  const ConfirmDeleteModal = (values) => {
    openConfirmModal({
      title: 'Confirm',
      styles: () => ({
        title: {
          fontSize: '22px',
          fontWeight: 'bold',
        },
      }),
      children: <Text size="sm">Are you sure you disable this territory?</Text>,
      confirmProps: { color: 'red' },
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onConfirm: () => {
        deleteMutate(values);
      },
    });
  };

  const { mutate: createMutate, isLoading: isCreating } = useMutation({
    mutationFn: async (values) => await createTerritory(values),
    onSuccess: () => {
      onUpdate();
      callRefetch();
    },
    onError: (error) => {
      NotificationUtil({
        success: false,
        title: 'Error',
        message: error.response.data.message,
      });
    },
  });

  const { mutate: updateMutate, isLoading: isUpdating } = useMutation({
    mutationFn: async (values) => await updateTerritory(values),
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      NotificationUtil({
        success: false,
        title: 'Error',
        message: error.response.data.message,
      });
    },
  });

  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation({
    mutationFn: async (values) => await toggleTerritory(values),
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      NotificationUtil({
        success: false,
        title: 'Error',
        message: error.response.data.message,
      });
    },
  });

  const callRefetch = async () => {
    await queryClient.refetchQueries({
      queryKey: ['region-pagelessData'],
      type: 'active',
    });
    await queryClient.refetchQueries({
      queryKey: ['area-pagelessData'],
      type: 'active',
    });
    await queryClient.refetchQueries({
      queryKey: ['distributor-pagelessData'],
      type: 'active',
    });
  };

  return (
    <div
      style={{
        height: '90vh',
      }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="accounts-input">
          <Controller
            name="regionId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={
                  regionPagelessdata?.map((item) => {
                    return {
                      label: item.name,
                      value: item.id,
                    };
                  }) || []
                }
                searchable
                label="Region"
                placeholder="Select region"
                dropdownPosition="bottom"
              />
            )}
          />
          {errors && errors.regionId && <p>{errors?.regionId?.message}</p>}
        </div>
        <div className="accounts-input">
          <Controller
            name="areaId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={
                  areaPagelessData
                    ?.filter((item) => item?.regionId === regionId)
                    ?.map((item) => {
                      return {
                        label: item.name,
                        value: item.id,
                      };
                    }) || []
                }
                searchable
                label="Area"
                placeholder="Select area"
                dropdownPosition="bottom"
              />
            )}
          />
          {errors && errors.areaId && <p>{errors?.areaId?.message}</p>}
        </div>
        <div className="accounts-input">
          <Controller
            name="distributorId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={
                  distributorPagelessdata
                    ?.filter((item) => item?.areaId === areaId)
                    ?.map((item) => {
                      return {
                        label: item.name,
                        value: item.id,
                      };
                    }) || []
                }
                searchable
                label="Distributor"
                placeholder="Select distributor"
                dropdownPosition="bottom"
              />
            )}
          />
          {errors && errors.distributorId && (
            <p>{errors?.distributorId?.message}</p>
          )}
        </div>
        <div className="accounts-input">
          <TextInput
            {...register('name')}
            label="Name"
            placeholder="Enter name"
            error={errors?.name?.message}
          />
        </div>

        <Flex gap={15} py={8} mt={10}>
          {update && (
            <Button
              disabled={isSubmitting}
              sx={{
                flex: 1,
              }}
              onClick={() => {
                ConfirmDeleteModal({
                  status: !defaultValues.isActive,
                  id: defaultValues.id,
                });
              }}
              color={defaultValues.isActive ? 'red' : 'green'}>
              {defaultValues.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting || isCreating || isUpdating || isDeleting}
            disabled={isSubmitting || !isValid}
            sx={{
              flex: 1,
              backgroundColor: COLORS.primary,
            }}>
            {update ? 'Update' : 'Create'}
          </Button>
        </Flex>
      </form>
    </div>
  );
};

export default TerritoriesForm;
